import { ButtonTertiary, ButtonDestructive, NovaButtonProps } from "@nova/component-btn"
import * as Icons from "@nova/component-icons"
import { HighPriorityNovaModalContainer } from "@nova/component-nova-modal-container"
import { useContext, ReactNode, useState, useRef, createContext, FC } from "react"

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyConfirmationModalOptions = ConfirmationModalOptions<any>

type ConfirmationModalOptions<T = never> = {
    RenderComponent: FC<{
        confirm: [T] extends [never] ? () => void : (args: T) => void
        cancel: () => void
        isPerformingAsyncConfirmCallback: boolean
        isPerformingAsyncCancelCallback: boolean
    }>
    asyncConfirmCallback?: [T] extends [never] ? () => Promise<void> : (args: T) => Promise<void>
    asyncCancelCallback?: () => Promise<void>
}

export type ConfirmationModalProps = Parameters<ConfirmationModalOptions["RenderComponent"]>[0]

type StackEntry = {
    id: number
    options: AnyConfirmationModalOptions
    resolve: (didConfirm: boolean, args?: unknown) => Promise<void>
    isPerformingAsyncConfirmCallback: boolean
    isPerformingAsyncCancelCallback: boolean
}

type ConfirmationModalContext = {
    isOpen: boolean
    confirm: <T = never>(options: ConfirmationModalOptions<T>) => Promise<boolean>
}

const ConfirmationModalContext = createContext<ConfirmationModalContext | null>(null)

export function useConfirmationModalContext() {
    const ctx = useContext(ConfirmationModalContext)
    if (!ctx)
        throw new Error(
            "useConfirmationModalContext must be used within WorkspaceConfirmationModalV2Provider"
        )
    return ctx
}

export function WorkspaceConfirmationModalV2Provider({ children }: { children: ReactNode }) {
    const [stack, setStack] = useState<StackEntry[]>([])
    const nextId = useRef(0)

    function confirm<T = never>(options: ConfirmationModalOptions<T>) {
        const id = nextId.current++
        return new Promise<boolean>((resolve, reject) => {
            const entry: StackEntry = {
                id,
                options: options as AnyConfirmationModalOptions,
                isPerformingAsyncConfirmCallback: false,
                isPerformingAsyncCancelCallback: false,
                resolve: async (didConfirm: boolean, args?: unknown) => {
                    if (didConfirm && options.asyncConfirmCallback) {
                        setStack((prev) =>
                            prev.map((e) =>
                                e.id === id ? { ...e, isPerformingAsyncConfirmCallback: true } : e
                            )
                        )
                        try {
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            await (options.asyncConfirmCallback as (args: any) => Promise<void>)(
                                args
                            )
                        } catch (err) {
                            setStack((prev) =>
                                prev.map((e) =>
                                    e.id === id
                                        ? { ...e, isPerformingAsyncConfirmCallback: false }
                                        : e
                                )
                            )
                            reject(err)
                            return
                        }
                    }
                    if (!didConfirm && options.asyncCancelCallback) {
                        setStack((prev) =>
                            prev.map((e) =>
                                e.id === id ? { ...e, isPerformingAsyncCancelCallback: true } : e
                            )
                        )
                        try {
                            await options.asyncCancelCallback()
                        } catch (err) {
                            setStack((prev) =>
                                prev.map((e) =>
                                    e.id === id
                                        ? { ...e, isPerformingAsyncCancelCallback: false }
                                        : e
                                )
                            )
                            reject(err)
                            return
                        }
                    }
                    resolve(didConfirm)
                },
            }
            setStack((prev) => [...prev, entry])
        })
    }

    const handleClose = async (entry: StackEntry, result: boolean, args?: unknown) => {
        try {
            await entry.resolve(result, args)
        } finally {
            setStack((prev) => prev.filter((e) => e.id !== entry.id))
        }
    }

    return (
        <ConfirmationModalContext.Provider value={{ confirm, isOpen: stack.length > 0 }}>
            {children}
            {stack.map((entry, index) => (
                <entry.options.RenderComponent
                    key={index}
                    confirm={(args?: unknown) => handleClose(entry, true, args)}
                    cancel={() => handleClose(entry, false)}
                    isPerformingAsyncConfirmCallback={entry.isPerformingAsyncConfirmCallback}
                    isPerformingAsyncCancelCallback={entry.isPerformingAsyncCancelCallback}
                />
            ))}
        </ConfirmationModalContext.Provider>
    )
}

type GenericConfirmationModalParams = {
    title: string
    message: string
    ConfirmButtonComponent?: FC<NovaButtonProps>
    confirmText?: string
    leftConfirmIcon?: Icons.IconComponent | null
    rightConfirmIcon?: Icons.IconComponent | null
    CancelButtonComponent?: FC<NovaButtonProps>
    cancelText?: string
    leftCancelIcon?: Icons.IconComponent | null
    rightCancelIcon?: Icons.IconComponent | null
}

export function getGenericConfirmationModalComponent({
    title,
    message,
    ConfirmButtonComponent = ButtonDestructive,
    confirmText = "Delete",
    leftConfirmIcon = Icons.Trash,
    rightConfirmIcon,
    CancelButtonComponent = ButtonTertiary,
    cancelText = "Cancel",
    leftCancelIcon,
    rightCancelIcon,
}: GenericConfirmationModalParams) {
    const GenericConfirmationModalComponent = ({
        confirm,
        cancel,
        isPerformingAsyncConfirmCallback,
        isPerformingAsyncCancelCallback,
    }: {
        confirm: () => void
        cancel: () => void
        isPerformingAsyncConfirmCallback: boolean
        isPerformingAsyncCancelCallback: boolean
    }) => {
        return (
            <HighPriorityNovaModalContainer
                title={title}
                bottomBar={
                    <div className="w-full flex justify-end items-center gap-2">
                        <CancelButtonComponent
                            title={cancelText}
                            onClick={cancel}
                            LeftIcon={leftCancelIcon || undefined}
                            RightIcon={rightCancelIcon || undefined}
                            disabled={
                                isPerformingAsyncConfirmCallback || isPerformingAsyncCancelCallback
                            }
                            isLoading={isPerformingAsyncCancelCallback}
                        />
                        <ConfirmButtonComponent
                            title={confirmText}
                            onClick={confirm}
                            LeftIcon={leftConfirmIcon || undefined}
                            RightIcon={rightConfirmIcon || undefined}
                            disabled={
                                isPerformingAsyncConfirmCallback || isPerformingAsyncCancelCallback
                            }
                            isLoading={isPerformingAsyncConfirmCallback}
                        />
                    </div>
                }
                closeModal={cancel}
                className="max-w-2xl"
            >
                <div className="mt-4">
                    <h4 className="text-center">{message}</h4>
                </div>
            </HighPriorityNovaModalContainer>
        )
    }
    return GenericConfirmationModalComponent
}

export function getGenericDangerConfirmationModalComponent({
    title,
    message,
    dangerMessage,
    ConfirmButtonComponent = ButtonDestructive,
    confirmText = "Delete",
    leftConfirmIcon = Icons.Trash,
    rightConfirmIcon,
    CancelButtonComponent = ButtonTertiary,
    cancelText = "Cancel",
    leftCancelIcon,
    rightCancelIcon,
}: GenericConfirmationModalParams & { dangerMessage: string }) {
    const GenericConfirmationModalComponent = ({
        confirm,
        cancel,
        isPerformingAsyncConfirmCallback,
        isPerformingAsyncCancelCallback,
    }: {
        confirm: () => void
        cancel: () => void
        isPerformingAsyncConfirmCallback: boolean
        isPerformingAsyncCancelCallback: boolean
    }) => {
        return (
            <HighPriorityNovaModalContainer
                title={title}
                bottomBar={
                    <div className="w-full flex justify-end items-center gap-2">
                        <CancelButtonComponent
                            title={cancelText}
                            onClick={cancel}
                            LeftIcon={leftCancelIcon || undefined}
                            RightIcon={rightCancelIcon || undefined}
                            disabled={
                                isPerformingAsyncConfirmCallback || isPerformingAsyncCancelCallback
                            }
                            isLoading={isPerformingAsyncCancelCallback}
                        />
                        <ConfirmButtonComponent
                            title={confirmText}
                            onClick={confirm}
                            LeftIcon={leftConfirmIcon || undefined}
                            RightIcon={rightConfirmIcon || undefined}
                            disabled={
                                isPerformingAsyncConfirmCallback || isPerformingAsyncCancelCallback
                            }
                            isLoading={isPerformingAsyncConfirmCallback}
                        />
                    </div>
                }
                closeModal={cancel}
                className="max-w-2xl"
            >
                <div className="mt-4 flex flex-col justify-center gap-3">
                    <h4 className="text-center">{message}</h4>
                    <h4 className="text-center text-red-500">{dangerMessage}</h4>
                </div>
            </HighPriorityNovaModalContainer>
        )
    }
    return GenericConfirmationModalComponent
}
